import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Edit, Save, X, Upload } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
export const PLACEHOLDER_IMG = 'data:image/svg+xml;utf8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect width="100%" height="100%" fill="#f1f5f9"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="20" fill="#94a3b8">Nessuna immagine</text></svg>'
);

export const EditableText = ({ type = 'text', value, onSave, label, multiline = false }) => {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleSave = () => {
    onSave(editValue);
    setIsEditing(false);
    toast({
      title: "Salvato!",
      description: `${label} aggiornato`,
    });
  };

  return (
    <>
      <div className="editable-wrapper">
        <div className="editable-content">{value}</div>
        <button 
          className="editable-trigger"
          onClick={() => {
            setEditValue(value);
            setIsEditing(true);
          }}
        >
          <Edit className="w-4 h-4" />
          <span>Modifica {label}</span>
        </button>
      </div>

      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica {label}</DialogTitle>
          </DialogHeader>
          <div className="edit-dialog-body">
            {multiline ? (
              <Textarea
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                rows={4}
                autoFocus
              />
            ) : (
              <Input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                autoFocus
              />
            )}
            <div className="edit-dialog-actions">
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                <X className="w-4 h-4 mr-2" />
                Annulla
              </Button>
              <Button onClick={handleSave}>
                <Save className="w-4 h-4 mr-2" />
                Salva
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const EditableImage = ({ src, alt, onSave, label, className = '' }) => {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [imageUrl, setImageUrl] = useState(src);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/settings/upload-image`, formData);
      const newUrl = `${BACKEND_URL}${response.data.url}`;
      setImageUrl(newUrl);
      setUploaded(true);
      toast({
        title: "Immagine caricata!",
        description: "Ora clicca Salva per applicarla",
      });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast({
        title: "Errore caricamento",
        description: typeof detail === 'string' ? detail : "Impossibile caricare l'immagine. Riprova con un file JPG o PNG",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSave = () => {
    onSave(imageUrl);
    setIsEditing(false);
    toast({
      title: "Salvato!",
      description: `${label} aggiornata`,
    });
  };

  return (
    <>
      <div className="editable-image-wrapper" style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img
          src={src || PLACEHOLDER_IMG}
          alt={alt}
          className={className}
          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = PLACEHOLDER_IMG; }}
        />
        <button 
          className="editable-image-trigger"
          data-testid="change-image-btn"
          onClick={() => {
            setImageUrl(src);
            setUploaded(false);
            setIsEditing(true);
          }}
        >
          <Upload className="w-4 h-4" />
          <span>Cambia Immagine</span>
        </button>
      </div>

      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica {label}</DialogTitle>
          </DialogHeader>
          <div className="edit-dialog-body">
            <div className="image-preview">
              <img
                src={imageUrl || PLACEHOLDER_IMG}
                alt="Preview"
                data-testid="image-preview"
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = PLACEHOLDER_IMG; }}
              />
              {uploaded && (
                <p className="text-sm" style={{ color: '#059669', marginTop: '6px' }} data-testid="upload-ready-msg">
                  ✓ Immagine pronta: clicca Salva per applicarla
                </p>
              )}
            </div>

            <div className="image-upload-section">
              <p><strong>Carica nuova immagine:</strong></p>
              <Input
                type="file"
                accept="image/*,.heic,.heif"
                onChange={handleUpload}
                disabled={uploading}
                data-testid="image-file-input"
              />
              {uploading && <p className="text-sm text-gray-500">Caricamento in corso...</p>}
            </div>

            <div className="image-url-section">
              <p><strong>Oppure inserisci URL:</strong></p>
              <Input
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://esempio.com/immagine.jpg"
              />
            </div>

            <div className="edit-dialog-actions">
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Annulla
              </Button>
              <Button onClick={handleSave} disabled={uploading} data-testid="image-save-btn">
                <Save className="w-4 h-4 mr-2" />
                Salva
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const EditableList = ({ items = [], onSave, label }) => {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editItems, setEditItems] = useState(items);
  const [newItem, setNewItem] = useState('');

  const handleAddItem = () => {
    if (newItem.trim()) {
      setEditItems([...editItems, newItem.trim().toUpperCase()]);
      setNewItem('');
    }
  };

  const handleRemoveItem = (itemToRemove) => {
    setEditItems(editItems.filter(item => item !== itemToRemove));
  };

  const handleSave = () => {
    onSave(editItems);
    setIsEditing(false);
    toast({
      title: "Salvato!",
      description: `${label} aggiornata`,
    });
  };

  return (
    <>
      <button 
        className="editable-trigger"
        onClick={() => {
          setEditItems(items);
          setIsEditing(true);
        }}
        style={{ marginTop: '12px' }}
      >
        <Edit className="w-4 h-4" />
        <span>Modifica Lista Prodotti</span>
      </button>

      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent style={{ maxWidth: '500px' }}>
          <DialogHeader>
            <DialogTitle>Modifica {label}</DialogTitle>
          </DialogHeader>
          <div className="edit-dialog-body">
            {/* Add new item */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <Input
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddItem()}
                placeholder="Aggiungi prodotto (es. KIWI GO+)"
                style={{ flex: 1 }}
              />
              <Button onClick={handleAddItem} type="button" size="sm">
                Aggiungi
              </Button>
            </div>

            {/* Items list */}
            <div style={{
              maxHeight: '300px',
              overflowY: 'auto',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '8px'
            }}>
              {editItems.length > 0 ? (
                editItems.map((item) => (
                  <div
                    key={`edit-item-${item}`}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 12px',
                      background: '#f8fafc',
                      borderRadius: '6px',
                      marginBottom: '6px'
                    }}
                  >
                    <span style={{ fontSize: '14px', color: '#1e293b' }}>
                      {item}
                    </span>
                    <Button
                      onClick={() => handleRemoveItem(item)}
                      variant="ghost"
                      size="sm"
                      style={{ padding: '4px', color: '#ef4444' }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              ) : (
                <p style={{
                  textAlign: 'center',
                  color: '#94a3b8',
                  fontSize: '13px',
                  padding: '20px'
                }}>
                  Nessun prodotto nella lista
                </p>
              )}
            </div>

            <div className="edit-dialog-actions">
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                <X className="w-4 h-4 mr-2" />
                Annulla
              </Button>
              <Button onClick={handleSave}>
                <Save className="w-4 h-4 mr-2" />
                Salva
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
